import { useRef, useEffect, useMemo } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// Green shades for longs, red shades for shorts
const LONG_COLORS = ['#22c55e', '#16a34a', '#15803d', '#166534', '#4ade80', '#86efac'];
const SHORT_COLORS = ['#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#f87171', '#fca5a5'];

export default function AllocationChart({ positions }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const { labels, data, colors } = useMemo(() => {
    const entries = [];
    let longIdx = 0, shortIdx = 0;

    for (const pos of positions) {
      const sym = pos.symbol.replace('USD', '');
      const side = pos.side === 'buy' ? 'LONG' : 'SHORT';
      const md = Math.pow(10, pos.moneyDigits || 2);
      const margin = pos.usedMargin
        ? pos.usedMargin / md
        : Math.abs(pos.currentPrice || pos.entryPrice) * (pos.volume / 100) / 30;
      const color = pos.side === 'buy'
        ? LONG_COLORS[longIdx++ % LONG_COLORS.length]
        : SHORT_COLORS[shortIdx++ % SHORT_COLORS.length];
      entries.push({ label: `${sym} ${side}`, margin, color });
    }

    return {
      labels: entries.map(e => e.label),
      data: entries.map(e => e.margin),
      colors: entries.map(e => e.color),
    };
  }, [positions]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    Chart.defaults.color = 'rgba(255,255,255,0.6)';
    Chart.defaults.borderColor = 'rgba(255,255,255,0.06)';

    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [{ data: [], backgroundColor: [], borderWidth: 0 }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: { legend: { position: 'bottom', labels: { padding: 12, font: { size: 11 } } } },
      },
    });

    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, []);

  useEffect(() => {
    if (!chartRef.current || labels.length === 0) return;
    chartRef.current.data.labels = labels;
    chartRef.current.data.datasets[0].data = data;
    chartRef.current.data.datasets[0].backgroundColor = colors;
    chartRef.current.update('none');
  }, [labels.join(','), data.join(','), colors.join(',')]);

  return (
    <div className="card bg-base-200 shadow-xl">
      <div className="card-body">
        <h2 className="card-title text-sm opacity-70">Allocation</h2>
        <div className="h-[260px] flex items-center justify-center">
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>
    </div>
  );
}
